import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Crown, Plus, ShieldCheck, UserMinus, Clock } from '@phosphor-icons/react';
import { api, useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';

const AdminTeamPage = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const [readiness, setReadiness] = useState(null);
  const [preparing, setPreparing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [team, ready] = await Promise.all([
        api.get('/admin/team'),
        api.get('/admin/team/sign-in-readiness').catch(() => null),
      ]);
      setMembers(team.data);
      setReadiness(ready?.data || null);
    } catch (error) {
      toast.error('Failed to load the team');
    } finally {
      setLoading(false);
    }
  }, []);

  const prepareAccounts = async () => {
    setPreparing(true);
    try {
      const response = await api.post('/admin/team/sign-in-readiness/prepare');
      const { prepared, already_existed: existed, failed } = response.data;
      toast.success(`${prepared} account${prepared === 1 ? '' : 's'} prepared, ${existed} already there`);
      if (failed?.length) toast.error(`${failed.length} could not be prepared`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not prepare accounts');
    } finally {
      setPreparing(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const promote = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const response = await api.post('/admin/team', { email: email.trim().toLowerCase() });
      toast.success(`${response.data.email} can now sign in as an admin`);
      setEmail('');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add that admin');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (member) => {
    if (!window.confirm(`Remove admin rights from ${member.email}? They stay on as an investor.`)) return;
    try {
      await api.delete(`/admin/team/${member.id}`);
      toast.success(`${member.email} is no longer an admin`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove admin rights');
    }
  };

  const canManage = user?.is_super_admin;

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]" data-testid="admin-team-page">
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-2xl font-heading font-semibold">Team &amp; Admins</h1>
          <p className="text-sm text-foreground-muted">
            Anyone here can reach the admin portal. Admin rights need a @flippex.com address.
          </p>
        </div>

        {canManage && (
          <Card className="p-6 bg-white mb-6">
            <form onSubmit={promote} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="colleague@flippex.com"
                  required
                  className="h-11"
                  data-testid="promote-email-input"
                />
                <p className="text-xs text-foreground-muted mt-2">
                  They can be added before they have ever signed in — the account is waiting when
                  they request their first code.
                </p>
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="h-11 rounded-full px-6 bg-primary hover:bg-primary-hover shrink-0"
                data-testid="promote-submit-button"
              >
                <Plus size={17} className="mr-2" /> {busy ? 'Adding...' : 'Make admin'}
              </Button>
            </form>
          </Card>
        )}

        {readiness && (
          <Card className="p-6 bg-white mb-6" data-testid="sign-in-readiness-card">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="font-semibold">Sign-in readiness</h2>
                <p className="text-sm text-foreground-muted mt-1">
                  Accounts are matched to the code we email by address, so every existing investor
                  keeps their history as long as their address is unambiguous.
                </p>
                <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4 text-sm">
                  <span data-testid="readiness-accounts">
                    <strong>{readiness.accounts}</strong> accounts
                  </span>
                  <span data-testid="readiness-signed-in">
                    <strong>{readiness.already_signed_in_with_a_code}</strong> have signed in with a code
                  </span>
                  <span data-testid="readiness-awaiting">
                    <strong>{readiness.awaiting_clerk_account}</strong> not prepared yet
                  </span>
                </div>

                {readiness.duplicate_addresses?.length > 0 && (
                  <p className="text-sm text-red-600 mt-3" data-testid="readiness-duplicates">
                    {readiness.duplicate_addresses.length} address
                    {readiness.duplicate_addresses.length === 1 ? '' : 'es'} used by more than one
                    account — merge them before go-live, or one person will land in the wrong one:{' '}
                    {readiness.duplicate_addresses.map((d) => d.email).join(', ')}
                  </p>
                )}
                {readiness.unusable_addresses?.length > 0 && (
                  <p className="text-sm text-red-600 mt-2" data-testid="readiness-unusable">
                    {readiness.unusable_addresses.length} account
                    {readiness.unusable_addresses.length === 1 ? '' : 's'} have no usable email
                    address and cannot receive a code.
                  </p>
                )}
                {readiness.ready && readiness.awaiting_clerk_account === 0 && (
                  <p className="text-sm text-green-700 mt-3" data-testid="readiness-all-clear">
                    Everyone can sign in.
                  </p>
                )}
              </div>

              {canManage && readiness.awaiting_clerk_account > 0 && (
                <Button
                  onClick={prepareAccounts}
                  disabled={preparing}
                  variant="outline"
                  className="h-11 rounded-full px-5 border-black/10 shrink-0"
                  data-testid="prepare-accounts-button"
                >
                  {preparing ? 'Preparing...' : 'Prepare accounts'}
                </Button>
              )}
            </div>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
          </div>
        ) : (
          <Card className="bg-white divide-y divide-black/5" data-testid="team-list">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center gap-4 p-5"
                data-testid={`team-member-row-${member.email}`}
              >
                <div
                  className={`h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${
                    member.is_super_admin ? 'bg-primary/10' : 'bg-slate-100'
                  }`}
                >
                  {member.is_super_admin ? (
                    <Crown size={20} weight="fill" className="text-primary" />
                  ) : (
                    <ShieldCheck size={20} className="text-slate-500" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{member.full_name}</p>
                    {member.is_super_admin && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary">
                        Super admin
                      </span>
                    )}
                    {!member.has_signed_in && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-yellow-100 text-yellow-700"
                        title="Has not signed in yet"
                      >
                        <Clock size={11} /> Invited
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-foreground-muted truncate">{member.email}</p>
                </div>

                {canManage && !member.is_super_admin && (
                  <button
                    onClick={() => revoke(member)}
                    className="shrink-0 inline-flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
                    data-testid={`revoke-admin-${member.email}`}
                  >
                    <UserMinus size={16} /> Remove
                  </button>
                )}
              </div>
            ))}
          </Card>
        )}

        {!canManage && (
          <p className="text-xs text-foreground-muted mt-4" data-testid="team-readonly-note">
            Only the super admin can add or remove admins.
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminTeamPage;
